(() => {
  if (customElements.get('dc-product-popouts')) return;
  let sequence = 0;
  const clean = (value) => value.replace(/\s+/g, ' ').trim();
  const syncScroll = () => document.documentElement.classList.toggle(
    'dc-popout-open', !!document.querySelector('dialog.dc-popout[open]')
  );

  class ProductPopouts extends HTMLElement {
    connectedCallback() {
      // Custom elements may connect before their children have been parsed.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else queueMicrotask(() => this.init());
    }

    disconnectedCallback() { syncScroll(); }

    init() {
      if (!this.isConnected || this.dataset.ready || !window.HTMLDialogElement) return;
      const source = this.querySelector('[data-dc-description]');
      if (!source) return;
      this.dataset.ready = 'true';
      const groups = [];

      // Find the original supplier paragraph and its material list, without
      // replacing the rest of the product's description or hardcoding sizes.
      // Suppliers sometimes use plain text, <br>, nested spans, or no space
      // between Spreadshirt and Kollektion. Locate the text independently
      // of its HTML wrapper, including a shared paragraph with the motif.
      const marker = /Die\s*Spreadshirt[\s–—-]*Kollektion\s*steht/i.exec(source.textContent);
      let startNode = null;
      let startOffset = 0;
      if (marker) {
        const walker = document.createTreeWalker(source, NodeFilter.SHOW_TEXT);
        let offset = marker.index;
        let node;
        while ((node = walker.nextNode())) {
          if (offset < node.textContent.length) {
            startNode = node;
            startOffset = offset;
            break;
          }
          offset -= node.textContent.length;
        }
      }
      const list = startNode && [...source.querySelectorAll('ul, ol')].find((node) =>
        !node.contains(startNode) &&
        !!(startNode.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING) &&
        /Material\s*:/i.test(node.textContent) && /Baumwolle/i.test(node.textContent)
      );
      if (startNode && list) {
        const range = document.createRange();
        range.setStart(startNode, startOffset);
        range.setEndAfter(list);
        // Avoid swallowing an unrelated table between the paragraph and list.
        if (!range.cloneContents().querySelector('table')) {
          groups.push(['Produktdetails & Material', range.extractContents()]);
        }
      }

      const table = [...source.querySelectorAll('table')].find((node) => {
        const cells = [...node.querySelectorAll('tr:first-child th, tr:first-child td')]
          .map((cell) => clean(cell.textContent).toUpperCase());
        return ['A', 'B', 'C'].every((label) => cells.includes(label)) &&
          [...node.querySelectorAll('tr')].some((row) =>
            /^(XS|S|M|L|XL|XXL|3XL|4XL|5XL)$/.test(clean(row.cells[0]?.textContent || ''))
          );
      });
      if (table) {
        const range = document.createRange();
        range.setStartBefore(table);
        range.setEndAfter(table);
        const images = [...source.querySelectorAll('img')].filter((image) =>
          !!(image.compareDocumentPosition(table) & Node.DOCUMENT_POSITION_FOLLOWING)
        );
        const image = images.at(-1);
        if (image) {
          const between = document.createRange();
          between.setStartAfter(image);
          between.setEndBefore(table);
          const fragment = between.cloneContents();
          // Only include a preceding image when it belongs directly to the
          // chart; unrelated product artwork stays visible.
          if (!fragment.querySelector('img, table, ul, ol') &&
              /^(normal|Größentabelle|Größenübersicht|Size chart)?$/i.test(clean(fragment.textContent))) {
            const wrapper = image.closest('p, figure');
            const start = wrapper && source.contains(wrapper) && !wrapper.contains(table) &&
              wrapper.querySelectorAll('img').length === 1 && !clean(wrapper.textContent)
              ? wrapper : image;
            range.setStartBefore(start);
          }
        }
        groups.push(['Größentabelle', range.extractContents()]);
      }

      if (!groups.length) return; // Unrecognized content remains fully visible.
      const links = document.createElement('div');
      links.className = 'dc-popout-links';
      groups.forEach(([label, content]) => {
        const id = `dc-product-dialog-${++sequence}`;
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'dc-popout-trigger';
        trigger.textContent = `${label} ↗`;
        trigger.setAttribute('aria-haspopup', 'dialog');
        trigger.setAttribute('aria-controls', id);
        const dialog = document.createElement('dialog');
        dialog.id = id;
        dialog.className = 'dc-popout';
        dialog.setAttribute('aria-labelledby', `${id}-title`);
        const header = document.createElement('div');
        header.className = 'dc-popout-header';
        const title = document.createElement('h2');
        title.id = `${id}-title`;
        title.textContent = label;
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'dc-popout-close';
        close.textContent = '×';
        close.setAttribute('aria-label', 'Schließen');
        close.autofocus = true;
        const body = document.createElement('div');
        body.className = 'dc-popout-body rte';
        body.append(content);
        body.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
        header.append(title, close);
        dialog.append(header, body);
        trigger.addEventListener('click', () => { dialog.showModal(); syncScroll(); });
        close.addEventListener('click', () => dialog.close());
        dialog.addEventListener('close', () => { syncScroll(); if (trigger.isConnected) trigger.focus(); });
        let backdropPress = false;
        const outside = (event) => {
          const rect = dialog.getBoundingClientRect();
          return event.clientX < rect.left || event.clientX > rect.right ||
            event.clientY < rect.top || event.clientY > rect.bottom;
        };
        dialog.addEventListener('pointerdown', (event) => { backdropPress = event.target === dialog && outside(event); });
        dialog.addEventListener('click', (event) => {
          if (backdropPress && event.target === dialog && outside(event)) dialog.close();
          backdropPress = false;
        });
        links.append(trigger);
        this.append(dialog);
      });
      this.append(links);
    }
  }
  customElements.define('dc-product-popouts', ProductPopouts);
})();
